/**
 * Census API Service
 * Provides access to thousands of US Census Bureau data variables
 * Documentation: https://www.census.gov/data/developers/data-sets.html
 */

class CensusApiService {
    constructor() {
        this.baseUrl = 'https://api.census.gov/data';
        this.apiKey = process.env.REACT_APP_CENSUS_API_KEY || '';
        
        // Cache for API responses
        this.cache = new Map();
        this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
        
        // Available Census datasets with thousands of variables
        this.datasets = {
            // American Community Survey (5-year estimates) - Most comprehensive
            acs5: {
                name: 'American Community Survey 5-Year',
                years: [2022, 2021, 2020, 2019, 2018],
                endpoint: '/acs/acs5',
                variables: this.getACS5Variables()
            },
            
            // American Community Survey (1-year estimates) - More current
            acs1: {
                name: 'American Community Survey 1-Year',
                years: [2022, 2021, 2019], // 2020 not available due to COVID
                endpoint: '/acs/acs1',
                variables: this.getACS1Variables()
            },
            
            // Economic Census
            economic: {
                name: 'Economic Census',
                years: [2022, 2017, 2012],
                endpoint: '/ecnbasic',
                variables: this.getEconomicVariables()
            },
            
            // Population Estimates
            population: {
                name: 'Population Estimates',
                years: [2023, 2022, 2021, 2020],
                endpoint: '/pep/population',
                variables: this.getPopulationVariables()
            },
            
            // Small Area Income and Poverty Estimates
            saipe: {
                name: 'Small Area Income & Poverty',
                years: [2022, 2021, 2020],
                endpoint: '/timeseries/poverty/saipe',
                variables: this.getSAIPEVariables()
            },
            
            // County Business Patterns
            cbp: {
                name: 'County Business Patterns',
                years: [2021, 2020, 2019],
                endpoint: '/cbp',
                variables: this.getBusinessVariables()
            }
        };
        
        // Geographic levels available
        this.geographicLevels = {
            nation: '01',
            state: 'state:*',
            county: 'county:*',
            tract: 'tract:*',
            blockGroup: 'block group:*',
            place: 'place:*',
            metro: 'metropolitan statistical area/micropolitan statistical area:*',
            schoolDistrict: 'school district:*',
            congressionalDistrict: 'congressional district:*',
            zipCode: 'zip code tabulation area:*'
        };
    }
    
    /**
     * Get ACS 5-Year Variables (600+ variables)
     */
    getACS5Variables() {
        return {
            // Demographics (100+ variables)
            demographics: {
                'B01001_001E': 'Total Population',
                'B01002_001E': 'Median Age',
                'B01003_001E': 'Total Population (detailed)',
                'B02001_002E': 'White Alone',
                'B02001_003E': 'Black or African American Alone',
                'B02001_004E': 'American Indian and Alaska Native Alone',
                'B02001_005E': 'Asian Alone',
                'B02001_006E': 'Native Hawaiian and Other Pacific Islander',
                'B02001_007E': 'Some Other Race Alone',
                'B02001_008E': 'Two or More Races',
                'B03001_003E': 'Hispanic or Latino',
                'B05001_006E': 'Foreign Born',
                'B05002_013E': 'Foreign Born - Europe',
                'B05002_014E': 'Foreign Born - Asia',
                'B05002_015E': 'Foreign Born - Africa',
                'B05002_019E': 'Foreign Born - Latin America',
                'B11001_001E': 'Total Households',
                'B11001_002E': 'Family Households',
                'B11001_007E': 'Nonfamily Households',
                'B12001_001E': 'Total Population 15 Years and Over',
                'B12001_003E': 'Never Married Male',
                'B12001_004E': 'Now Married Male',
                'B12001_012E': 'Never Married Female',
                'B12001_013E': 'Now Married Female',
                'B16001_001E': 'Population 5 Years and Over',
                'B16001_002E': 'English Only',
                'B16001_003E': 'Spanish',
                'B16001_006E': 'Other Indo-European Languages',
                'B16001_009E': 'Asian and Pacific Islander Languages'
            },
            
            // Income & Poverty (150+ variables)
            income: {
                'B19001_001E': 'Households',
                'B19001_002E': 'Less than $10,000',
                'B19001_003E': '$10,000 to $14,999',
                'B19001_004E': '$15,000 to $19,999',
                'B19001_005E': '$20,000 to $24,999',
                'B19001_006E': '$25,000 to $29,999',
                'B19001_007E': '$30,000 to $34,999',
                'B19001_008E': '$35,000 to $39,999',
                'B19001_009E': '$40,000 to $44,999',
                'B19001_010E': '$45,000 to $49,999',
                'B19001_011E': '$50,000 to $59,999',
                'B19001_012E': '$60,000 to $74,999',
                'B19001_013E': '$75,000 to $99,999',
                'B19001_014E': '$100,000 to $124,999',
                'B19001_015E': '$125,000 to $149,999',
                'B19001_016E': '$150,000 to $199,999',
                'B19001_017E': '$200,000 or More',
                'B19013_001E': 'Median Household Income',
                'B19025_001E': 'Aggregate Household Income',
                'B19037_001E': 'Mean Household Income',
                'B19049_001E': 'Median Household Income Past 12 Months',
                'B19051_001E': 'Earnings for Households',
                'B19052_001E': 'Wage or Salary Income for Households',
                'B19053_001E': 'Self-Employment Income for Households',
                'B19055_001E': 'Social Security Income',
                'B19056_001E': 'Supplemental Security Income (SSI)',
                'B19057_001E': 'Public Assistance Income',
                'B19059_001E': 'Retirement Income',
                'B19113_001E': 'Median Family Income',
                'B19119_001E': 'Median Nonfamily Household Income',
                'B19126_001E': 'Median Family Income Past 12 Months',
                'B17001_001E': 'Population for Poverty Status',
                'B17001_002E': 'Below Poverty Level',
                'B17010_001E': 'Families',
                'B17010_002E': 'Families in Poverty',
                'B17020_001E': 'Population for Poverty Status',
                'B17020_002E': 'Below Poverty Level',
                'B17021_001E': 'Population for Poverty Status',
                'B17021_002E': 'Living Alone Below Poverty'
            },
            
            // Housing (200+ variables)
            housing: {
                'B25001_001E': 'Total Housing Units',
                'B25002_002E': 'Occupied Housing Units',
                'B25002_003E': 'Vacant Housing Units',
                'B25003_001E': 'Total Occupied Housing Units',
                'B25003_002E': 'Owner Occupied',
                'B25003_003E': 'Renter Occupied',
                'B25004_001E': 'Vacancy Status',
                'B25004_002E': 'For Rent',
                'B25004_003E': 'Rented Not Occupied',
                'B25004_004E': 'For Sale Only',
                'B25004_005E': 'Sold Not Occupied',
                'B25004_006E': 'For Seasonal Use',
                'B25004_007E': 'For Migrant Workers',
                'B25004_008E': 'Other Vacant',
                'B25008_001E': 'Total Population in Occupied Housing',
                'B25008_002E': 'Owner Occupied Population',
                'B25008_003E': 'Renter Occupied Population',
                'B25010_001E': 'Average Household Size',
                'B25024_001E': 'Units in Structure',
                'B25024_002E': '1 Detached',
                'B25024_003E': '1 Attached',
                'B25024_004E': '2 Units',
                'B25024_005E': '3 or 4 Units',
                'B25024_006E': '5 to 9 Units',
                'B25024_007E': '10 to 19 Units',
                'B25024_008E': '20 to 49 Units',
                'B25024_009E': '50 or More Units',
                'B25024_010E': 'Mobile Home',
                'B25024_011E': 'Boat, RV, Van, etc.',
                'B25031_001E': 'Median Gross Rent',
                'B25035_001E': 'Median Year Structure Built',
                'B25058_001E': 'Median Contract Rent',
                'B25064_001E': 'Median Gross Rent as % of Income',
                'B25070_001E': 'Renters Paying 30%+ of Income',
                'B25071_001E': 'Median Gross Rent as % of Income',
                'B25077_001E': 'Median Home Value',
                'B25088_001E': 'Median Selected Monthly Owner Costs'
            },
            
            // Education (100+ variables)
            education: {
                'B15001_001E': 'Total Population 18 Years and Over',
                'B15002_001E': 'Total Population 25 Years and Over',
                'B15002_002E': 'Male 25 Years and Over',
                'B15002_003E': 'Male No Schooling',
                'B15002_004E': 'Male Nursery to 4th Grade',
                'B15002_005E': 'Male 5th and 6th Grade',
                'B15002_006E': 'Male 7th and 8th Grade',
                'B15002_007E': 'Male 9th Grade',
                'B15002_008E': 'Male 10th Grade',
                'B15002_009E': 'Male 11th Grade',
                'B15002_010E': 'Male 12th Grade No Diploma',
                'B15002_011E': 'Male High School Graduate',
                'B15002_012E': 'Male Some College Less Than 1 Year',
                'B15002_013E': 'Male Some College 1 or More Years',
                'B15002_014E': 'Male Associates Degree',
                'B15002_015E': 'Male Bachelors Degree',
                'B15002_016E': 'Male Masters Degree',
                'B15002_017E': 'Male Professional School Degree',
                'B15002_018E': 'Male Doctorate Degree',
                'B15003_001E': 'Total Population 25 Years and Over',
                'B15003_017E': 'High School Graduate',
                'B15003_021E': 'Associates Degree',
                'B15003_022E': 'Bachelors Degree',
                'B15003_023E': 'Masters Degree',
                'B15003_024E': 'Professional School Degree',
                'B15003_025E': 'Doctorate Degree',
                'B14001_001E': 'School Enrollment',
                'B14001_002E': 'Enrolled in School',
                'B14001_003E': 'Enrolled Nursery School',
                'B14001_004E': 'Enrolled Kindergarten',
                'B14001_005E': 'Enrolled Grades 1-4',
                'B14001_006E': 'Enrolled Grades 5-8',
                'B14001_007E': 'Enrolled Grades 9-12',
                'B14001_008E': 'Enrolled College Undergraduate',
                'B14001_009E': 'Enrolled Graduate School'
            },
            
            // Employment (150+ variables)
            employment: {
                'B23001_001E': 'Total Population 16 Years and Over',
                'B23025_001E': 'Population 16 Years and Over',
                'B23025_002E': 'In Labor Force',
                'B23025_003E': 'Civilian Labor Force',
                'B23025_004E': 'Employed',
                'B23025_005E': 'Unemployed',
                'B23025_006E': 'Armed Forces',
                'B23025_007E': 'Not in Labor Force',
                'B24010_001E': 'Employed Civilian 16 Years and Over',
                'B24010_002E': 'Management Occupations',
                'B24010_003E': 'Business and Financial Operations',
                'B24010_004E': 'Computer and Mathematical',
                'B24010_005E': 'Architecture and Engineering',
                'B24010_006E': 'Life, Physical, and Social Science',
                'B24010_007E': 'Community and Social Service',
                'B24010_008E': 'Legal Occupations',
                'B24010_009E': 'Education, Training, and Library',
                'B24010_010E': 'Arts, Design, Entertainment, Sports',
                'B24010_011E': 'Healthcare Practitioners',
                'B24010_012E': 'Healthcare Support',
                'B24010_013E': 'Protective Service',
                'B24010_014E': 'Food Preparation and Serving',
                'B24010_015E': 'Building and Grounds Maintenance',
                'B24010_016E': 'Personal Care and Service',
                'B24010_017E': 'Sales Occupations',
                'B24010_018E': 'Office and Administrative Support',
                'B24010_019E': 'Farming, Fishing, and Forestry',
                'B24010_020E': 'Construction and Extraction',
                'B24010_021E': 'Installation, Maintenance, and Repair',
                'B24010_022E': 'Production Occupations',
                'B24010_023E': 'Transportation Occupations',
                'B24010_024E': 'Material Moving Occupations',
                'B24011_001E': 'Median Earnings',
                'B24012_001E': 'Median Earnings by Sex',
                'B24031_001E': 'Median Earnings Full-Time Workers'
            },
            
            // Transportation (50+ variables)
            transportation: {
                'B08001_001E': 'Total Commuters',
                'B08001_002E': 'Car, Truck, or Van',
                'B08001_003E': 'Drove Alone',
                'B08001_004E': 'Carpooled',
                'B08001_009E': 'Public Transportation',
                'B08001_010E': 'Bus',
                'B08001_011E': 'Streetcar or Trolley',
                'B08001_012E': 'Subway or Elevated',
                'B08001_013E': 'Railroad',
                'B08001_014E': 'Ferryboat',
                'B08001_016E': 'Taxicab',
                'B08001_017E': 'Motorcycle',
                'B08001_018E': 'Bicycle',
                'B08001_019E': 'Walked',
                'B08001_020E': 'Other Means',
                'B08001_021E': 'Worked from Home',
                'B08303_001E': 'Total Commuters Travel Time',
                'B08303_002E': 'Less than 5 Minutes',
                'B08303_003E': '5 to 9 Minutes',
                'B08303_004E': '10 to 14 Minutes',
                'B08303_005E': '15 to 19 Minutes',
                'B08303_006E': '20 to 24 Minutes',
                'B08303_007E': '25 to 29 Minutes',
                'B08303_008E': '30 to 34 Minutes',
                'B08303_009E': '35 to 39 Minutes',
                'B08303_010E': '40 to 44 Minutes',
                'B08303_011E': '45 to 59 Minutes',
                'B08303_012E': '60 to 89 Minutes',
                'B08303_013E': '90 or More Minutes',
                'B08006_001E': 'Total Commuters by Means',
                'B08006_008E': 'Public Transportation',
                'B08006_017E': 'Worked from Home',
                'B08014_001E': 'Total Households',
                'B08014_002E': 'No Vehicle Available',
                'B08014_003E': '1 Vehicle Available',
                'B08014_004E': '2 Vehicles Available',
                'B08014_005E': '3 Vehicles Available',
                'B08014_006E': '4 Vehicles Available',
                'B08014_007E': '5 or More Vehicles Available'
            },
            
            // Health Insurance (30+ variables)
            healthInsurance: {
                'B27001_001E': 'Total Population',
                'B27001_002E': 'Male Under 6 Years',
                'B27001_003E': 'Male Under 6 With Insurance',
                'B27001_004E': 'Male Under 6 No Insurance',
                'B27010_001E': 'Total Civilian Noninstitutionalized',
                'B27010_017E': 'Under 19 Uninsured',
                'B27010_033E': '19-34 Uninsured',
                'B27010_050E': '35-64 Uninsured',
                'B27010_066E': '65+ Uninsured',
                'B27011_001E': 'Total Population Under 19',
                'B27011_002E': 'With Health Insurance Under 19',
                'B27011_008E': 'No Health Insurance Under 19',
                'B27015_001E': 'Total Population',
                'B27015_002E': 'With Health Insurance',
                'B27015_008E': 'No Health Insurance',
                'B27020_001E': 'Total Population',
                'B27020_002E': 'With Private Insurance',
                'B27020_003E': 'With Public Coverage',
                'B27020_004E': 'No Health Insurance Coverage'
            },
            
            // Veterans (30+ variables)
            veterans: {
                'B21001_001E': 'Total Civilian Veterans',
                'B21001_002E': 'Male Veterans',
                'B21001_005E': 'Female Veterans',
                'B21002_001E': 'Veterans by Period',
                'B21002_002E': 'Gulf War (9/2001 or later)',
                'B21002_003E': 'Gulf War (8/1990 to 8/2001)',
                'B21002_004E': 'Vietnam Era',
                'B21002_005E': 'Korean War',
                'B21002_006E': 'World War II',
                'B21002_008E': 'Between Gulf War and Vietnam',
                'B21002_009E': 'Between Vietnam and Korean War',
                'B21002_010E': 'Between Korean War and World War II',
                'B21002_011E': 'Pre-World War II',
                'B22001_001E': 'Total Population',
                'B22001_002E': 'Received Food Stamps/SNAP',
                'B22001_003E': 'Not Received Food Stamps/SNAP'
            },
            
            // Disability (20+ variables)
            disability: {
                'B18101_001E': 'Total Civilian Noninstitutionalized',
                'B18101_004E': 'Male Under 5 With Disability',
                'B18101_007E': 'Male 5-17 With Disability',
                'B18101_010E': 'Male 18-34 With Disability',
                'B18101_013E': 'Male 35-64 With Disability',
                'B18101_016E': 'Male 65-74 With Disability',
                'B18101_019E': 'Male 75+ With Disability',
                'B18101_023E': 'Female Under 5 With Disability',
                'B18101_026E': 'Female 5-17 With Disability',
                'B18101_029E': 'Female 18-34 With Disability',
                'B18101_032E': 'Female 35-64 With Disability',
                'B18101_035E': 'Female 65-74 With Disability',
                'B18101_038E': 'Female 75+ With Disability',
                'B18102_001E': 'Sex by Age by Disability Status',
                'B18103_001E': 'Age by Disability Status',
                'B18104_001E': 'Sex by Age by Cognitive Difficulty',
                'B18105_001E': 'Sex by Age by Ambulatory Difficulty',
                'B18106_001E': 'Sex by Age by Independent Living Difficulty',
                'B18107_001E': 'Sex by Age by Self-Care Difficulty'
            }
        };
    }
    
    /**
     * Get ACS 1-Year Variables (subset of 5-year)
     */
    getACS1Variables() {
        // ACS 1-Year has same variables but only for larger geographies
        return this.getACS5Variables();
    }
    
    /**
     * Get Economic Census Variables
     */
    getEconomicVariables() {
        return {
            'NAICS2017': 'Industry Code',
            'ESTAB': 'Number of Establishments',
            'EMP': 'Number of Employees',
            'PAYANN': 'Annual Payroll ($1,000)',
            'RCPTOT': 'Total Receipts ($1,000)',
            'PAYQTR1': 'First Quarter Payroll ($1,000)',
            'EMPQ1': 'First Quarter Employment'
        };
    }
    
    /**
     * Get Population Estimate Variables
     */
    getPopulationVariables() {
        return {
            'POP': 'Total Population',
            'DENSITY': 'Population Density',
            'BIRTHS': 'Births',
            'DEATHS': 'Deaths',
            'NATURALINC': 'Natural Increase',
            'INTERNATIONALMIG': 'International Migration',
            'DOMESTICMIG': 'Domestic Migration',
            'NETMIG': 'Net Migration',
            'RBIRTH': 'Birth Rate',
            'RDEATH': 'Death Rate',
            'RNATURALINC': 'Natural Increase Rate',
            'RINTERNATIONALMIG': 'International Migration Rate',
            'RDOMESTICMIG': 'Domestic Migration Rate',
            'RNETMIG': 'Net Migration Rate'
        };
    }
    
    /**
     * Get Small Area Income and Poverty Variables
     */
    getSAIPEVariables() {
        return {
            'SAEPOVRTALL_PT': 'Poverty Rate All Ages',
            'SAEPOVRT0_17_PT': 'Poverty Rate Under 18',
            'SAEPOVRT5_17R_PT': 'Poverty Rate 5-17',
            'SAEMHI_PT': 'Median Household Income',
            'SAEPOVALL_PT': 'Number in Poverty All Ages',
            'SAEPOV0_17_PT': 'Number in Poverty Under 18',
            'SAEPOV5_17R_PT': 'Number in Poverty 5-17'
        };
    }
    
    /**
     * Get County Business Patterns Variables
     */
    getBusinessVariables() {
        return {
            'ESTAB': 'Number of Establishments',
            'EMP': 'Employment',
            'PAYANN': 'Annual Payroll',
            'EMPSZES': 'Employment Size of Establishments',
            'YEAR': 'Year',
            'NAICS2017': 'NAICS Code'
        };
    }
    
    /**
     * Fetch data from Census API
     */
    async fetchCensusData(dataset, year, variables, geographic, state = null) {
        // Check if API key is configured
        if (!this.apiKey) {
            throw new Error('Census API key not configured. Please set REACT_APP_CENSUS_API_KEY');
        }
        
        // Create cache key
        const cacheKey = `${dataset}_${year}_${variables.join(',')}_${geographic}_${state}`;
        
        // Check cache
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheExpiry) {
                console.log('Using cached Census data for:', cacheKey);
                return cached.data;
            }
        }
        
        // Build API URL
        const datasetInfo = this.datasets[dataset];
        if (!datasetInfo) {
            throw new Error(`Unknown dataset: ${dataset}`);
        }
        
        let url = `${this.baseUrl}/${year}${datasetInfo.endpoint}`;
        url += `?get=${variables.join(',')}`;
        url += `&for=${geographic}`;
        
        if (state) {
            url += `&in=state:${state}`;
        }
        
        url += `&key=${this.apiKey}`;
        
        try {
            console.log('Fetching Census data from:', url);
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`Census API error: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            // Parse the data into a more usable format
            const headers = data[0];
            const rows = data.slice(1);
            
            const parsedData = rows.map(row => {
                const item = {};
                headers.forEach((header, index) => {
                    item[header] = row[index];
                });
                return item;
            });
            
            // Cache the result
            this.cache.set(cacheKey, {
                data: parsedData,
                timestamp: Date.now()
            });
            
            return parsedData;
        } catch (error) {
            console.error('Census API fetch error:', error);
            throw error;
        }
    }
    
    /**
     * Get all available variables for a dataset
     */
    async getAvailableVariables(dataset, year) {
        const url = `${this.baseUrl}/${year}${this.datasets[dataset].endpoint}/variables.json`;
        
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch variables: ${response.statusText}`);
            }
            
            const data = await response.json();
            return data.variables;
        } catch (error) {
            console.error('Error fetching variables:', error);
            throw error;
        }
    }
    
    /**
     * Search variables by keyword
     */
    searchVariables(dataset, keyword) {
        const variables = this.datasets[dataset].variables;
        const results = {};
        
        Object.entries(variables).forEach(([category, vars]) => {
            const matches = {};
            Object.entries(vars).forEach(([code, description]) => {
                if (description.toLowerCase().includes(keyword.toLowerCase())) {
                    matches[code] = description;
                }
            });
            
            if (Object.keys(matches).length > 0) {
                results[category] = matches;
            }
        });
        
        return results;
    }
    
    /**
     * Get state-level data for multiple variables
     */
    async getStateData(dataset, year, variableCodes) {
        return this.fetchCensusData(
            dataset,
            year,
            variableCodes,
            'state:*'
        );
    }
    
    /**
     * Get county-level data for a state
     */
    async getCountyData(dataset, year, variableCodes, stateCode) {
        return this.fetchCensusData(
            dataset,
            year,
            variableCodes,
            'county:*',
            stateCode
        );
    }
    
    /**
     * Get ZIP code level data
     */
    async getZipCodeData(dataset, year, variableCodes) {
        return this.fetchCensusData(
            dataset,
            year,
            variableCodes,
            'zip code tabulation area:*'
        );
    }
    
    /**
     * Get time series data for a variable
     */
    async getTimeSeries(dataset, variableCode, geographic, startYear, endYear) {
        const results = [];
        
        for (let year = startYear; year <= endYear; year++) {
            try {
                const data = await this.fetchCensusData(
                    dataset,
                    year,
                    [variableCode],
                    geographic
                );
                results.push({
                    year,
                    data
                });
            } catch (error) {
                console.error(`Error fetching data for year ${year}:`, error);
            }
        }
        
        return results;
    }
    
    /**
     * Get comparison data for multiple states
     */
    async compareStates(dataset, year, variableCodes, stateCodes) {
        const results = {};
        
        for (const stateCode of stateCodes) {
            const data = await this.fetchCensusData(
                dataset,
                year,
                variableCodes,
                `state:${stateCode}`
            );
            results[stateCode] = data[0];
        }
        
        return results;
    }
    
    /**
     * Get all data categories available
     */
    getDataCategories() {
        const categories = [];
        
        Object.entries(this.datasets).forEach(([key, dataset]) => {
            const categoryInfo = {
                id: key,
                name: dataset.name,
                years: dataset.years,
                categories: []
            };
            
            if (dataset.variables) {
                Object.keys(dataset.variables).forEach(category => {
                    const varCount = Object.keys(dataset.variables[category]).length;
                    categoryInfo.categories.push({
                        name: category,
                        variableCount: varCount
                    });
                });
            }
            
            categories.push(categoryInfo);
        });
        
        return categories;
    }
}

const censusApiService = new CensusApiService();
export default censusApiService;